import { cameraDistanceForLens, lensForCameraDistance } from '../renderer/src/camera-lens.js';
import { evaluateAuthoredCameraTrack } from '../renderer/src/camera-keyframes.js';
import { paidCameraGimbal, type CameraGimbalId } from '../renderer/src/camera-gimbal.js';
import type { CameraDistanceId, CameraPlatformId } from '../renderer/src/presets.js';
import type { CinematicSequence, CinematicOverrides } from '../renderer/src/cinematic-sequence.js';
import { exportFrameProgress, resolveExportTiming, type ExportRange } from '../renderer/src/export-range.js';

export interface GimbalUsageInput {
  cameraGimbal?: CameraGimbalId | undefined; cameraStyle?: string | undefined; camera?: string | undefined;
  cameraDistance?: string | undefined; distance?: string | undefined; durationSeconds?: number | undefined;
  cinematicSequence?: CinematicSequence | undefined; exportRange?: ExportRange | undefined;
}
export interface TimedGimbalWaypoint { seconds: number; effectiveDurationSeconds: number; transitionStartSeconds?: number; waypoint: { overrides: CinematicOverrides } }
export interface GimbalUsageContext {
  initialOverrides?: CinematicOverrides | undefined;
  firstProgress: number; lastProgress: number; durationSeconds: number; waypoints: readonly TimedGimbalWaypoint[];
}
interface State { mode: CameraGimbalId; distance: CameraDistanceId; style: CameraPlatformId }
/** Examines rendered moments, including both contributors to a smooth camera transition. */
export function requiresPaidGimbal(output: GimbalUsageInput, context?: GimbalUsageContext): boolean {
  if (output.cinematicSequence?.cameraTracks?.version === 1) return false;
  const overrides = context?.initialOverrides;
  const initial: State = { mode: overrides?.cameraGimbal ?? output.cameraGimbal ?? 'dynamic',
    distance: (overrides?.cameraDistance ?? output.cameraDistance ?? output.distance ?? 'standard') as CameraDistanceId,
    style: (overrides?.cameraStyle ?? output.cameraStyle ?? output.camera ?? 'orbit') as CameraPlatformId };
  const paid = (state: State, seconds = 0) => {
    const tracks = output.cinematicSequence?.cameraTracks;
    const lens = tracks?.version === 2 ? evaluateAuthoredCameraTrack(tracks, 'lens', seconds, () => lensForCameraDistance(state.distance)) : undefined;
    return paidCameraGimbal(state.mode, lens === undefined ? state.distance : cameraDistanceForLens(lens), state.style);
  };
  let previous = initial;
  const segments = (context?.waypoints ?? []).map(item => {
    const overrides = item.waypoint.overrides, from = previous;
    const to: State = { mode: overrides.cameraGimbal ?? from.mode, distance: overrides.cameraDistance ?? from.distance,
      style: overrides.cameraStyle ?? from.style };
    previous = to; return { ...item, from, to };
  });
  if (!output.durationSeconds) return paid(initial);
  const timing = resolveExportTiming({ durationSeconds: output.durationSeconds, exportRange: output.exportRange });
  for (let frame = 0; frame < timing.totalFrames; frame++) {
    const sourceProgress = exportFrameProgress(timing, frame);
    const progress = context ? Math.max(0, Math.min(1, (sourceProgress - context.firstProgress) / (context.lastProgress - context.firstProgress))) : sourceProgress;
    const seconds = progress * (context?.durationSeconds ?? output.durationSeconds);
    const lensSeconds = sourceProgress * output.durationSeconds;
    const segment = segments.findLast(item => seconds + 1e-9 >= (item.transitionStartSeconds ?? item.seconds));
    if (!segment) { if (paid(initial, lensSeconds)) return true; continue; }
    const amount = segment.effectiveDurationSeconds === 0 ? 1 : Math.max(0, Math.min(1, (seconds - (segment.transitionStartSeconds ?? segment.seconds)) / segment.effectiveDurationSeconds));
    if ((amount < 1 && paid(segment.from, lensSeconds)) || (amount > 0 && paid(segment.to, lensSeconds))) return true;
  }
  return false;
}
